import { useState } from 'react';
import type { User } from '../types';
import { subscriptionService } from '../services/subscriptionService';
import { CHECKOUT_CONFIG } from '../constants';

export type SubscriptionPlansPanelProps = {
  user: User | null;
  /** Optional path to return after payment (e.g. /bid/uuid). Passed to PRAV return URL. */
  returnToPath?: string | null;
};

function buildSubscriptionReturnUrl(returnToPath?: string | null): string {
  const base = `${window.location.origin}/subscription`;
  if (!returnToPath) return base;
  const params = new URLSearchParams({ returnTo: returnToPath });
  return `${base}?${params.toString()}`;
}

export function SubscriptionPlansPanel({ user, returnToPath }: SubscriptionPlansPanelProps) {
  const [loading, setLoading] = useState(false);
  const plan = user?.plan || 'FREE';

  const runCheckout = async (targetPlan: 'PREMIUM' | 'PRO') => {
    if (loading || !user) return;

    setLoading(true);
    try {
      const returnUrl = buildSubscriptionReturnUrl(returnToPath);
      const { checkoutUrl } = await subscriptionService.startExternalCheckout(targetPlan, returnUrl);

      window.location.href = checkoutUrl;
    } catch (e: unknown) {
      setLoading(false);
      alert(e instanceof Error ? e.message : 'Failed to start checkout');
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded-xl px-3 py-2">
        Paid plans open secure checkout on{' '}
        <span className="font-semibold text-slate-800">Prav Solutions</span> (PayU). After payment, return
        here — your plan updates within a minute.
      </p>

      <div
        className={`p-5 rounded-2xl border-2 ${plan === 'FREE' ? 'border-blue-200 bg-slate-50' : 'border-slate-200'}`}
      >
        <div className="flex justify-between items-start mb-3">
          <div>
            <h3 className="font-black text-lg">Free Plan</h3>
            <p className="text-[10px] font-bold text-slate-400 uppercase">Basic access</p>
          </div>
          {plan === 'FREE' && (
            <span className="text-[9px] font-black bg-blue-600 text-white px-3 py-1 rounded-full">ACTIVE</span>
          )}
        </div>
        <ul className="text-sm text-slate-600 space-y-2">
          <li>✓ Can create bids</li>
          <li className="text-red-600 font-semibold">✗ Cannot participate in bidding</li>
          <li>✓ All other features open</li>
        </ul>
      </div>

      <div
        className={`p-5 rounded-2xl border-2 ${plan === 'PREMIUM' ? 'border-blue-300 bg-blue-50/50' : 'border-slate-200'}`}
      >
        <div className="flex justify-between items-start mb-2">
          <div>
            <h3 className="font-black text-lg">Premium</h3>
            <p className="text-[10px] font-bold text-blue-600 uppercase">30 days access</p>
          </div>
          {plan === 'PREMIUM' ? (
            <span className="text-[9px] font-black bg-blue-600 text-white px-3 py-1 rounded-full">ACTIVE</span>
          ) : (
            <button
              type="button"
              disabled={loading}
              onClick={() => runCheckout('PREMIUM')}
              className="text-[10px] font-black bg-blue-600 text-white px-4 py-2 rounded-xl disabled:opacity-50"
            >
              {loading ? 'OPENING…' : 'SUBSCRIBE'}
            </button>
          )}
        </div>
        <p className="text-xl font-black">INR 599.00</p>
        <p className="text-[10px] text-slate-500 uppercase">inclusive of taxes</p>
      </div>

      <div
        className={`p-5 rounded-2xl border-2 relative ${plan === 'PRO' ? 'border-blue-600 bg-white' : 'bg-blue-600 border-blue-600 text-white'}`}
      >
        <div className="absolute top-0 right-0 bg-amber-400 text-slate-900 text-[8px] font-black px-3 py-1 rounded-bl-xl">
          SAVINGS
        </div>
        <div className="flex justify-between items-start mb-2 mt-2">
          <div>
            <h3 className={`font-black text-lg ${plan === 'PRO' ? 'text-slate-900' : ''}`}>Pro</h3>
            <p
              className={`text-[10px] font-bold uppercase ${plan === 'PRO' ? 'text-indigo-600' : 'text-white/90'}`}
            >
              90 days access
            </p>
          </div>
          {plan === 'PRO' ? (
            <span className="text-[9px] font-black bg-blue-600 text-white px-3 py-1 rounded-full">ACTIVE</span>
          ) : (
            <button
              type="button"
              disabled={loading}
              onClick={() => runCheckout('PRO')}
              className="text-[10px] font-black bg-white text-blue-600 px-4 py-2 rounded-xl disabled:opacity-50"
            >
              {loading ? 'OPENING…' : 'SUBSCRIBE'}
            </button>
          )}
        </div>
        <p className={`text-xl font-black ${plan === 'PRO' ? 'text-slate-900' : 'text-white'}`}>INR 1,599.00</p>
      </div>

      <p className="text-[10px] text-slate-400 text-center">
        Checkout hosted at {CHECKOUT_CONFIG.pravSubscribeHost}
      </p>
    </div>
  );
}
